<template>
  <td 
    class="editable-cell" 
    ref="cellRef" 
    :style="tdStyles"
    :class="{ 'is-editing': isFocused }"
  >
    <div class="cell-wrapper" v-click-outside="closeDropdown">
      <div
        class="cell-content"
        :class="{ 'cell-invalid': invalid }"
        :contenteditable="editable"
        :data-placeholder="placeholder"
        :title="(!isFocused && shouldShowTooltip) ? displayValue : null"
        ref="inputRef"
        @keydown.enter.prevent="selectHighlightedAndNext"
        @keydown.tab="handleTab"
        @keydown.down.prevent="navigateOptions(1)"
        @keydown.up.prevent="navigateOptions(-1)"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @mouseover="checkOverflow"
      >{{ displayValue }}</div>

      <div class="icon-container" @mousedown.prevent="toggleDropdown">
        <i class="fas fa-chevron-down"></i>
      </div>
    </div>
  </td>
</template>

<script>
import { EventBus } from '@/event-bus.js';

export default {
  name: 'EditableSelectCell',
  props: {
    name: { type: String, required: true },
    value: [String, Number],
    options: { type: Array, default: () => [] },
    editable: { type: Boolean, default: true },
    placeholder: { type: String, default: 'Select...' },
    optionKey: { type: String, default: 'key' },
    optionValue: { type: String, default: 'value' },
    maxWidth: { type: String, default: '200px' }
  },
  data() {
    return {
      invalid: false,
      showDropdown: false,
      isFocused: false,
      searchQuery: '',
      highlightedIndex: 0,
      shouldShowTooltip: false,
      dropdownElement: null
    };
  },
  computed: {
    tdStyles() {
      return { maxWidth: this.maxWidth, width: this.maxWidth };
    },
    displayValue() {
      const selected = this.options.find(o => o[this.optionKey] === this.value);
      return selected ? selected[this.optionValue] : '';
    },
    filteredOptions() {
      const query = this.searchQuery.toLowerCase().trim();
      return !query ? this.options : this.options.filter(opt => 
        String(opt[this.optionValue]).toLowerCase().includes(query)
      );
    },
    row() {
        var split = this.name.split('-');
        if (split.length === 3) {
            return split[1];
        }

        return -1;
    }
  },
  watch: {
    showDropdown(val) {
      val ? this.renderDropdown() : this.removeDropdown();
    },
    filteredOptions() { if (this.showDropdown) this.renderDropdown(); },
    highlightedIndex() { if (this.showDropdown) this.renderDropdown(); }
  },
  methods: {
    checkOverflow() {
      const el = this.$refs.inputRef;
      if (el) this.shouldShowTooltip = el.scrollWidth > (el.clientWidth + 1);
    },

    onInput(event) {
      this.searchQuery = event.target.innerText;
      this.showDropdown = true;
      this.highlightedIndex = 0;
    },

    onFocus() {
      this.isFocused = true;
      // Capturamos el texto actual para el filtro inicial
      this.searchQuery = this.$refs.inputRef.innerText.trim();

      // 1. REPARACIÓN: Usamos renderDropdown() que es el método que calcula 
      // la posición en el Body para el Portal.
      if (this.showDropdown) {
        this.renderDropdown();
      }

      // 2. SELECCIÓN DE TEXTO: Seleccionamos todo el contenido al entrar
      this.$nextTick(() => {
        const el = this.$refs.inputRef;
        if (el && window.getSelection) {
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(el);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      });

      this.$emit('focus-row', this.row);
    },

    onBlur() {
      this.isFocused = false;
      if (!this.showDropdown) this.processAutoSelection();

      this.$emit('focus-row', -1);
    },

    handleTab() {
      if (this.showDropdown) {
        this.processAutoSelection();
        this.closeDropdown();
      }
    },

    processAutoSelection() {
      const text = this.$refs.inputRef.innerText.trim().toLowerCase();
      if (text !== '') {
        const exact = this.options.find(o => 
          o[this.optionValue].toLowerCase() === text
        );
        if (exact) this.selectOption(exact, false);
        else if (this.filteredOptions.length > 0) 
          this.selectOption(this.filteredOptions[0], false);
        else this.clearCell();
      } else this.clearCell();
      this.invalidateState(false);
    },

    clearCell() {
      this.$emit('input', null);
      this.$refs.inputRef.innerText = '';
      this.emitChanged(null);
    },

    closeDropdown() {
      this.showDropdown = false;
      this.highlightedIndex = 0;
    },

    renderDropdown() {
      this.$nextTick(() => {
        if (!this.$refs.cellRef) return;
        const rect = this.$refs.cellRef.getBoundingClientRect();
        const listHeight = Math.min(this.filteredOptions.length * 40, 200);
        const spaceBelow = window.innerHeight - rect.bottom;
        const positionTop = spaceBelow < listHeight && rect.top > listHeight;
        
        const top = positionTop 
          ? (rect.top + window.scrollY - listHeight) 
          : (rect.bottom + window.scrollY);

        if (!this.dropdownElement) {
          this.dropdownElement = document.createElement('ul');
          this.dropdownElement.className = 'autocomplete-list-portal';
          document.body.appendChild(this.dropdownElement);
        }

        Object.assign(this.dropdownElement.style, {
          position: 'absolute', top: `${top}px`, left: `${rect.left + window.scrollX}px`,
          width: `${rect.width}px`, zIndex: '999999', display: 'block'
        });

        this.dropdownElement.innerHTML = this.filteredOptions.map((opt, i) => {
          const isHighlighted = i === this.highlightedIndex ? 'is-highlighted' : '';
          const val = opt[this.optionValue];
          return `<li class="${isHighlighted}" data-index="${i}" title="${val}">${this.highlightMatch(val)}</li>`;
        }).join('');

        // Item event listeners
        Array.from(this.dropdownElement.children).forEach(li => {
          li.onmousedown = (e) => { e.preventDefault(); this.selectOption(this.filteredOptions[li.dataset.index]); };
          // Check overflow for list items to show/hide tooltip
          li.onmouseover = () => {
            if (li.scrollWidth <= li.clientWidth) li.removeAttribute('title');
          };
        });
      });
    },

    removeDropdown() {
      if (this.dropdownElement?.parentNode) {
        this.dropdownElement.parentNode.removeChild(this.dropdownElement);
        this.dropdownElement = null;
      }
    },

    selectOption(option, triggerNext = true) {
      this.$emit('input', option[this.optionKey]);
      this.$refs.inputRef.innerText = option[this.optionValue];
      this.closeDropdown();
      this.emitChanged(option[this.optionKey]);
      if (triggerNext) this.$nextTick(() => this.$emit('next-cell'));
    },

    // Standard methods...
    highlightMatch(text) {
      if (!this.searchQuery) return text;
      const regex = new RegExp(`(${this.searchQuery})`, 'gi');
      return String(text).replace(regex, '<b class="match-highlight">$1</b>');
    },
    navigateOptions(direction) {
      if (!this.showDropdown) { this.showDropdown = true; return; }
      const len = this.filteredOptions.length;
      if (len > 0) this.highlightedIndex = (this.highlightedIndex + direction + len) % len;
    },
    selectHighlightedAndNext() {
      if (this.showDropdown && this.filteredOptions[this.highlightedIndex]) 
        this.selectOption(this.filteredOptions[this.highlightedIndex]);
      else this.$emit('next-cell');
    },
    toggleDropdown() {
      if (!this.editable) return;
      this.showDropdown = !this.showDropdown;
      if (this.showDropdown) this.$refs.inputRef.focus();
    },
    focusCell() {
      this.$refs.inputRef.focus();
    },
    emitChanged(val) {
      EventBus.$emit('onChanged', { Changed: true, ComponentName: this.name, Value: val });
    },
    invalidateState(state) {
        this.$nextTick(() => this.invalid = state);
    },
  },
  beforeDestroy() { this.removeDropdown(); },
  directives: {
    'click-outside': {
      bind(el, binding, vnode) {
        el.clickOutsideEvent = (e) => { if (!(el === e.target || el.contains(e.target))) vnode.context[binding.expression](); };
        document.body.addEventListener('mousedown', el.clickOutsideEvent);
      },
      unbind(el) { document.body.removeEventListener('mousedown', el.clickOutsideEvent); }
    }
  }
}
</script>

<style>
/* Portal styles (Global - reside en el body) */
.autocomplete-list-portal {
  background: white; 
  border: 1px solid #ccc; 
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  margin: 0; 
  padding: 0; 
  list-style: none; 
  max-height: 200px; 
  overflow-y: auto;
  z-index: 999999;
}

.autocomplete-list-portal li {
  padding: 8px 12px; 
  cursor: pointer; 
  font-size: 13px;
  white-space: nowrap; 
  overflow: hidden; 
  text-overflow: ellipsis;
  border-bottom: 1px solid #eee;
  color: #333; /* Texto normal en negro/gris oscuro */
}

/* Color del texto resaltado por defecto (Azul) */
.autocomplete-list-portal li .match-highlight {
  color: #007bff; 
  font-weight: bold;
}

/* --- SOLUCIÓN AL PROBLEMA DE VISIBILIDAD --- */
/* Cuando el LI está seleccionado o se le pasa el mouse, el fondo es azul */
.autocomplete-list-portal li.is-highlighted,
.autocomplete-list-portal li:hover {
  background-color: #007bff; 
  color: #ffffff !important; /* Todo el texto del LI a blanco */
}

/* Cambiamos el color de la coincidencia (negrita) a blanco cuando el fondo es azul */
.autocomplete-list-portal li.is-highlighted .match-highlight,
.autocomplete-list-portal li:hover .match-highlight {
  color: #ffffff !important;
}
</style>

<style scoped>
.editable-cell {
  position: relative; padding: 0 !important; vertical-align: middle;
}

/* Logic: Expand text when editing, truncate when not */
.editable-cell:not(.is-editing) {
  overflow: hidden;
}

.cell-wrapper {
  display: flex; align-items: center; position: relative; width: 100%; height: 100%;
}

.cell-content {
  flex: 1; padding: 8px 32px 8px 8px; outline: none;
  box-sizing: border-box; display: block;
}

/* Truncate only when not focused */
.editable-cell:not(.is-editing) .cell-content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Expand on focus */
.is-editing .cell-content {
  white-space: normal;
  word-break: break-word;
  position: relative;
  z-index: 10;
  background: white; /* Avoid seeing background text */
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  min-width: 100%;
  width: fit-content;
}

.cell-content:empty:before { content: attr(data-placeholder); color: #888; font-style: italic; }
.icon-container { position: absolute; right: 8px; color: #007bff; cursor: pointer; z-index: 11; }
.cell-invalid { background-color: #fff5f5; box-shadow: inset 0 0 0 1px red !important; }
</style>
